import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { BuildingStorefrontIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { RestaurantDetails } from '../types';
import { apiService } from '../services/api';
import { useToast } from '../context/ToastContext';
import { buildRestaurantFrontendOrigin } from '../utils/restaurantUtils';

interface ShopDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userMobile?: string;
}

const ShopDetailsModal: React.FC<ShopDetailsModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  userMobile 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useToast();
  const usernameManuallyEdited = useRef(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch
  } = useForm<RestaurantDetails>({
    defaultValues: {
      name: '',
      id: '',
      address: '',
      description: '',
      phone: userMobile ? `+91 ${userMobile}` : '',
      email: '',
      your_name: ''
    }
  });

  const shopId = watch('id');
  const shopName = watch('name');

  // Sync shop name to username whenever shop name changes (unless manually edited)
  useEffect(() => {
    if (shopName && shopName.trim() && !usernameManuallyEdited.current) {
      // Convert shop name to valid username format
      // Lowercase, replace spaces and special chars with hyphens, remove leading/trailing hyphens
      const formattedUsername = shopName
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9-]/g, '-') // Replace non-alphanumeric (except hyphens) with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
        .substring(0, 63); // Limit to 63 characters
      
      if (formattedUsername && formattedUsername.length > 0) {
        setValue('id', formattedUsername, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [shopName, setValue]);

  const shopIdField = register('id', { 
    required: 'Shop Username is required',
    pattern: {
      value: /^(?!-)(?!.*-$)(?![0-9]+$)[a-z0-9-]{1,63}$/,
      message:
        'Shop Username can contain letters (a–z), numbers (0–9), and hyphens. It cannot start or end with a hyphen, cannot be only numbers, and must be at most 63 characters.'
    },
    setValueAs: (value: string) => value?.toLowerCase().trim()
  });

  const previewUrl = useMemo(() => {
    if (!shopId || !shopId.trim()) {
      return '';
    }
    try {
      return buildRestaurantFrontendOrigin({ id: shopId.trim() });
    } catch (error) {
      console.warn('Failed to build preview URL', error);
      return '';
    }
  }, [shopId]);

  // Set phone number when userMobile is available
  useEffect(() => {
    if (userMobile) {
      setValue('phone', `+91 ${userMobile}`);
    }
  }, [userMobile, setValue]);

  // Handle browser back button to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = () => {
      onClose();
    };

    // Push history state when modal opens
    window.history.pushState({ modalOpen: true }, '');

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      usernameManuallyEdited.current = false;
      reset({
        name: '',
        id: '',
        address: '',
        description: '',
        phone: userMobile ? `+91 ${userMobile}` : '',
        email: '',
        your_name: ''
      });
    }
  }, [isOpen, reset, userMobile]);

  const onSubmit = async (data: RestaurantDetails) => {
    setIsLoading(true);
    try {
      // Build update payload with only the fields used in this modal
      // The API service will extract only the fields that are present
      // Format FSSAI number with prefix
      const fssaiNumber = data.description?.trim() || '';
      const formattedFssai = fssaiNumber ? `FSSAI License No. ${fssaiNumber}` : '';
      
      const updatePayload: any = {
        name: data.name,
        id: data.id,
        address: data.address,
        description: formattedFssai,
        email: data.email.trim(),
      };

      // Only include optional fields if they have values
      if (data.your_name && data.your_name.trim()) {
        updatePayload.your_name = data.your_name.trim();
      }

      const response = await apiService.updateRestaurantProfile(updatePayload);

      if (response.status) {
        // Update localStorage with new details including plan
        const updatedDetails = {
          name: data.name,
          id: data.id,
          address: data.address,
          description: formattedFssai,
          phone: data.phone,
          email: data.email,
          your_name: data.your_name,
          plan: 'basic' // Plan is set to basic when shop details are submitted
        };
        localStorage.setItem('restaurantDetails', JSON.stringify(updatedDetails));
        
        // showSuccess('Shop details updated successfully!');
        onSuccess();
        if (window.history.state?.modalOpen) {
          window.history.back();
        }
        onClose();
      } else {
        showError(response.message || 'Failed to update shop details');
      }
    } catch (error) {
      console.error('Error updating shop details:', error);
      showError('An error occurred while updating shop details');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-orange-600 px-6 py-4 rounded-t-xl">
          <div className="flex items-center">
            <BuildingStorefrontIcon className="h-6 w-6 text-white mr-3" />
            <h2 className="text-xl font-bold text-white">Complete Your Shop Details</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-600">
              Please complete your shop details to continue using the platform. 
              This information will be used for your business profile and customer interactions.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  {...register('your_name')}
                  type="text"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-base"
                  placeholder="Enter your name"
                />
                {errors.your_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.your_name.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shop Name *
                </label>
                <input
                  {...register('name', { required: 'Shop name is required' })}
                  type="text"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-base"
                  placeholder="Enter shop name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shop Username *
                </label>
                <input
                  {...shopIdField}
                  value={shopId || ''}
                  onChange={(event) => {
                    usernameManuallyEdited.current = true;
                    const normalizedValue = event.target.value
                      .toLowerCase()
                      .trim();
                    setValue('id', normalizedValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }}
                  type="text"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-base"
                  placeholder="Enter shop username"
                />
                {errors.id && (
                  <p className="mt-1 text-sm text-red-600">{errors.id.message}</p>
                )}
                <p className="mt-2 text-sm text-gray-600">
                  {previewUrl
                    ? (
                      <>
                        Your public store link will be{' '}
                        <span className="font-semibold text-gray-900 break-all">{previewUrl}</span>
                      </>
                      )
                    : 'Enter a unique username to generate your public store link.'}
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                FSSAI License Number *
                </label>
                <input
                  {...register('description', { 
                    required: 'FSSAI License Number is required',
                    pattern: {
                      value: /^\d{14}$/,
                      message: 'Enter a valid 14-digit FSSAI number'
                    }
                  })}
                  type='number'
                  maxLength={14}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-base"
                  placeholder="Enter 14-digit FSSAI License Number"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                )}
                <small className="text-gray-500">Enter a valid 14-digit FSSAI License Number.</small>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('phone', { required: 'Phone number is required' })}
                    type="tel"
                    className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed text-base"
                    placeholder="Phone number"
                    readOnly
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">Phone number is verified and cannot be changed</p>
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  {...register('email', { 
                    required: 'Email address is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address',
                    },
                  })}
                  type="email"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-base"
                  placeholder="e.g., support@menunix.com"
                />
                <p className="mt-1 text-sm text-gray-500">
                  This email will be shared with Razorpay for receipts and other payment notifications.
                </p>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                <textarea
                  {...register('address', { required: 'Address is required' })}
                  rows={2}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-base"
                  placeholder="e.g., 123 Main Street, New York, NY 10001"
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-orange-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Saving...' : 'Save Details'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ShopDetailsModal;





